import { useCallback, useEffect, useRef, useState } from "react";
import { snapFrameToAlignGuides } from "../../utils/canvasAlignGuides";
import {
  clampPositionAllowOverflow,
  computeCornerUniformScale,
  computeEdgeCrop,
  computeRotationFromPointers,
  isCornerHandle,
  isSideHandle,
} from "./objectTransformMath";

const EMPTY_GUIDES = { vertical: [], horizontal: [] };

/**
 * Pointer sessions for move / corner-scale / edge-crop / rotate.
 * Mirrors CanvasEditor object transform behavior (incl. center/middle snap guides).
 *
 * onChange({ x, y, width, height, rotation?, crop? })
 */
export function useObjectTransformInteraction({
  enabled = false,
  frame = null,
  crop = null,
  cropExtent = null,
  originalImageSize = null,
  stageSize = null,
  onChange = null,
  minSize = 10,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [alignGuides, setAlignGuides] = useState(EMPTY_GUIDES);

  const sessionRef = useRef(null);
  const rafRef = useRef(null);
  const frameRef = useRef(frame);
  const cropRef = useRef(crop);
  const cropExtentRef = useRef(cropExtent);
  const originalImageSizeRef = useRef(originalImageSize);
  const stageSizeRef = useRef(stageSize);
  const onChangeRef = useRef(onChange);

  frameRef.current = frame;
  cropRef.current = crop;
  cropExtentRef.current = cropExtent;
  originalImageSizeRef.current = originalImageSize;
  stageSizeRef.current = stageSize;
  onChangeRef.current = onChange;

  const emit = useCallback((partial) => {
    const emitFn = onChangeRef.current;
    if (typeof emitFn !== "function") return;
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      emitFn(partial);
    });
  }, []);

  const clearAlignGuides = useCallback(() => {
    setAlignGuides(EMPTY_GUIDES);
  }, []);

  const endSession = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    sessionRef.current = null;
    setIsDragging(false);
    setIsResizing(false);
    setIsRotating(false);
    setIsCropping(false);
    clearAlignGuides();
  }, [clearAlignGuides]);

  useEffect(() => {
    if (!enabled) {
      endSession();
    }
  }, [enabled, endSession]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const startMove = useCallback(
    (e) => {
      if (!enabled || !frameRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const f = frameRef.current;
      sessionRef.current = {
        mode: "move",
        startX: e.clientX,
        startY: e.clientY,
        initialPosition: { x: f.x ?? f.left ?? 0, y: f.y ?? f.top ?? 0 },
        initialSize: { width: f.width, height: f.height },
        initialRotation: Number(f.rotation) || 0,
      };
      setIsDragging(true);

      const onMove = (ev) => {
        const s = sessionRef.current;
        if (!s || s.mode !== "move") return;
        const dx = ev.clientX - s.startX;
        const dy = ev.clientY - s.startY;
        const stage = stageSizeRef.current;
        let x = s.initialPosition.x + dx;
        let y = s.initialPosition.y + dy;

        if (stage?.width > 0 && stage?.height > 0) {
          const snapped = snapFrameToAlignGuides(
            {
              x,
              y,
              width: s.initialSize.width,
              height: s.initialSize.height,
              rotation: s.initialRotation,
            },
            [],
            stage.width,
            stage.height
          );
          setAlignGuides(snapped.guides || EMPTY_GUIDES);
          const clamped = clampPositionAllowOverflow(
            snapped.x,
            snapped.y,
            s.initialSize.width,
            s.initialSize.height,
            stage.width,
            stage.height
          );
          x = clamped.x;
          y = clamped.y;
        } else {
          clearAlignGuides();
        }

        emit({
          x,
          y,
          width: s.initialSize.width,
          height: s.initialSize.height,
        });
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        endSession();
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [enabled, emit, endSession, clearAlignGuides]
  );

  const startResize = useCallback(
    (e, handle) => {
      if (!enabled || !frameRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const f = frameRef.current;
      const c = cropRef.current;
      const img = originalImageSizeRef.current;
      const side = isSideHandle(handle);
      const hasCrop =
        side &&
        c &&
        c.width > 0 &&
        c.height > 0 &&
        img &&
        img.width > 0 &&
        img.height > 0;

      sessionRef.current = {
        mode: "resize",
        handle,
        startX: e.clientX,
        startY: e.clientY,
        initialPosition: { x: f.x ?? f.left ?? 0, y: f.y ?? f.top ?? 0 },
        initialSize: { width: f.width, height: f.height },
        initialCrop: hasCrop
          ? { x: c.x, y: c.y, width: c.width, height: c.height }
          : null,
        originalImageSize: img
          ? { width: img.width, height: img.height }
          : null,
        cropExtent: cropExtentRef.current,
        edgeCrops: Boolean(hasCrop),
      };
      setIsResizing(true);
      setIsCropping(Boolean(hasCrop));

      const onMove = (ev) => {
        const s = sessionRef.current;
        if (!s || s.mode !== "resize") return;
        const dx = ev.clientX - s.startX;
        const dy = ev.clientY - s.startY;
        const stage = stageSizeRef.current;

        if (isCornerHandle(s.handle)) {
          const next = computeCornerUniformScale({
            handle: s.handle,
            dx,
            dy,
            initialSize: s.initialSize,
            initialPosition: s.initialPosition,
            canvasSize: stage,
            minSize,
          });
          emit({
            x: next.position.x,
            y: next.position.y,
            width: next.size.width,
            height: next.size.height,
          });
          return;
        }

        if (s.edgeCrops && s.initialCrop && s.originalImageSize) {
          const next = computeEdgeCrop({
            handle: s.handle,
            dx,
            dy,
            initialCrop: s.initialCrop,
            initialSize: s.initialSize,
            initialPosition: s.initialPosition,
            originalImageSize: s.originalImageSize,
            cropExtent: s.cropExtent,
            canvasSize: stage,
            minDisplay: minSize,
          });
          emit({
            x: next.position.x,
            y: next.position.y,
            width: next.size.width,
            height: next.size.height,
            crop: next.crop,
          });
          return;
        }

        // Fallback: edge stretch without crop (uniform height/width only)
        const aspect = s.initialSize.width / Math.max(1, s.initialSize.height);
        let width = s.initialSize.width;
        let height = s.initialSize.height;
        let x = s.initialPosition.x;
        let y = s.initialPosition.y;
        const h = s.handle;
        if (h === "edge-e") width = Math.max(minSize, s.initialSize.width + dx);
        else if (h === "edge-w") {
          width = Math.max(minSize, s.initialSize.width - dx);
          x = s.initialPosition.x + (s.initialSize.width - width);
        } else if (h === "edge-s")
          height = Math.max(minSize, s.initialSize.height + dy);
        else if (h === "edge-n") {
          height = Math.max(minSize, s.initialSize.height - dy);
          y = s.initialPosition.y + (s.initialSize.height - height);
        }
        if (stage?.width > 0) {
          const clamped = clampPositionAllowOverflow(
            x,
            y,
            width,
            height,
            stage.width,
            stage.height
          );
          x = clamped.x;
          y = clamped.y;
        }
        emit({ x, y, width, height, aspect });
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        endSession();
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [enabled, emit, endSession, minSize]
  );

  const startRotate = useCallback(
    (e) => {
      if (!enabled || !frameRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const chrome =
        e.currentTarget.closest(".object-transform-chrome") ||
        e.currentTarget.parentElement;
      const rect = chrome.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const startAngle =
        Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
      const f = frameRef.current;
      sessionRef.current = {
        mode: "rotate",
        centerX,
        centerY,
        startAngle,
        initialRotation: Number(f.rotation) || 0,
      };
      setIsRotating(true);

      const onMove = (ev) => {
        const s = sessionRef.current;
        if (!s || s.mode !== "rotate") return;
        const rotation = computeRotationFromPointers({
          clientX: ev.clientX,
          clientY: ev.clientY,
          centerX: s.centerX,
          centerY: s.centerY,
          startAngle: s.startAngle,
          initialRotation: s.initialRotation,
          shiftKey: ev.shiftKey,
        });
        const cur = frameRef.current;
        emit({
          x: cur?.x ?? cur?.left ?? 0,
          y: cur?.y ?? cur?.top ?? 0,
          width: cur?.width,
          height: cur?.height,
          rotation,
        });
      };

      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        endSession();
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [enabled, emit, endSession]
  );

  return {
    isDragging,
    isResizing,
    isRotating,
    isCropping,
    alignGuides,
    startMove,
    startResize,
    startRotate,
  };
}
